import React, { useEffect, useState, useCallback } from 'react';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import PageHeader     from '../../../components/common/PageHeader';
import AlertToast     from '../../../components/common/AlertToast';
import LoadingSpinner from '../../../components/common/LoadingSpinner';

// ── Constants ─────────────────────────────────────────────────────────────────
const ROLE_COLORS = {
  admin:        'danger',
  tenant_admin: 'danger',
  manager:      'warning',
  team_lead:    'info',
  faculty:      'primary',
  student:      'secondary',
  staff:        'dark',
};

const ROLE_LABELS = {
  admin:        'Admin',
  tenant_admin: 'Admin',
  manager:      'Manager',
  team_lead:    'Team Lead',
  faculty:      'Faculty',
  student:      'Student',
  staff:        'Staff',
};

const ALL_ROLES = ['admin', 'manager', 'team_lead', 'faculty', 'staff', 'student'];

const fmt    = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const initials = (u) => `${(u.firstName || '?')[0]}${(u.lastName || '?')[0]}`.toUpperCase();
const fullName = (u) => `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email;

const STATUS_LEAVE = {
  proposed:  'warning',
  approved:  'success',
  rejected:  'danger',
  cancelled: 'secondary',
};

// ── Employee Avatar ────────────────────────────────────────────────────────────
function Avatar({ user, size = 44 }) {
  const color = ROLE_COLORS[user.roleKey] || 'secondary';
  const bgMap = { danger: '#dc3545', warning: '#fd7e14', info: '#0dcaf0', primary: '#0d6efd', secondary: '#6c757d', dark: '#212529' };
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: bgMap[color] || '#6c757d',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontWeight: 700, fontSize: size * 0.35,
    }}>
      {initials(user)}
    </div>
  );
}

// ── Leave Mini-Summary ─────────────────────────────────────────────────────────
// `refreshKey` — bump to force a re-fetch (e.g., when the user applies a leave
// in another tab and comes back).
function LeaveSummary({ employeeId, refreshKey = 0 }) {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get('/hrm/leave', { params: { employeeId, limit: 5 } })
      .then(r => setLeaves(r.data?.records || r.data?.data || []))
      .catch(() => setLeaves([]))
      .finally(() => setLoading(false));
  }, [employeeId, refreshKey]);

  if (loading) return <div className="text-center py-2"><span className="spinner-border spinner-border-sm text-primary"></span></div>;
  if (!leaves.length) return <p className="text-muted small mb-0">No leave records.</p>;

  return (
    <div>
      {leaves.map(l => (
        <div key={l._id} className="d-flex justify-content-between align-items-center mb-1" style={{ fontSize: 12 }}>
          <span className="text-capitalize">
            {l.leaveType?.replace('_', ' ')} · {l.totalDays}d
            <small className="text-muted ms-1">
              ({fmt(l.startDate)}{l.startDate !== l.endDate ? ` → ${fmt(l.endDate)}` : ''})
            </small>
          </span>
          <span className={`badge bg-${STATUS_LEAVE[l.status] || 'secondary'}`}>{l.status}</span>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export default function EmployeesPage() {
  const { isAdmin, role } = useAuth();
  const canEdit = isAdmin || role?.key === 'manager';

  // ── State ──────────────────────────────────────────────────────────────────
  const [employees, setEmployees]     = useState([]);
  const [leaveCounts, setLeaveCounts] = useState({}); // employeeId → { proposed, approved, rejected, total, totalDays, onLeaveToday }
  const [loading, setLoading]         = useState(false);
  const [search, setSearch]           = useState('');
  const [filterRole, setFilterRole]   = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [editForm, setEditForm]       = useState(null);  // non-null when editing
  const [editSaving, setEditSaving]   = useState(false);
  const [alert, setAlert]             = useState({ show: false, msg: '', type: 'success' });
  // Bumping this triggers re-fetches in every LeaveSummary and re-loads the counts.
  const [refreshKey, setRefreshKey]   = useState(0);

  const showAlert = (msg, type = 'success') => setAlert({ show: true, msg, type });

  // ── Load employees ─────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/users', { params: { limit: 200 } });
      setEmployees(r.data?.records || r.data?.data || []);
    } catch {
      setEmployees([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Load leave counts per employee (admin/manager only) ────────────────────
  const loadLeaveCounts = useCallback(async () => {
    try {
      const r = await api.get('/hrm/leave/counts-by-employee');
      const rows = r.data?.data || r.data?.records || r.data || [];
      const map = {};
      (Array.isArray(rows) ? rows : []).forEach(row => {
        if (row.employeeId) map[row.employeeId] = row;
      });
      setLeaveCounts(map);
    } catch {
      // non-admins will get 403 — silently ignore, the badges just won't render.
      setLeaveCounts({});
    }
  }, []);

  useEffect(() => {
    if (canEdit) loadLeaveCounts();
  }, [canEdit, loadLeaveCounts, refreshKey]);

  // Re-fetch when the user returns to this tab — catches leaves applied elsewhere.
  useEffect(() => {
    const onFocus = () => setRefreshKey(k => k + 1);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  const handleRefresh = () => {
    setRefreshKey(k => k + 1);
    showAlert('Refreshed.', 'success');
  };

  // ── Filtered list ──────────────────────────────────────────────────────────
  const filtered = employees.filter(e => {
    const nameMatch = !search ||
      fullName(e).toLowerCase().includes(search.toLowerCase()) ||
      (e.email || '').toLowerCase().includes(search.toLowerCase());
    const roleMatch = !filterRole || e.roleKey === filterRole;
    return nameMatch && roleMatch;
  });

  // ── Stats ──────────────────────────────────────────────────────────────────
  const total    = employees.length;
  const active   = employees.filter(e => e.isActive !== false).length;
  const roleCounts = ALL_ROLES.reduce((acc, r) => {
    acc[r] = employees.filter(e => e.roleKey === r).length;
    return acc;
  }, {});

  // ── Edit employee ──────────────────────────────────────────────────────────
  const openEdit = (e) => {
    setEditForm({ firstName: e.firstName, lastName: e.lastName, email: e.email, phone: e.phone || '', isActive: e.isActive !== false });
    setSelectedEmployee(e);
  };

  const saveEdit = async () => {
    if (!selectedEmployee || !editForm) return;
    setEditSaving(true);
    try {
      await api.put(`/users/${selectedEmployee._id}`, editForm);
      showAlert('Employee updated successfully');
      setEditForm(null);
      load();
    } catch (err) {
      showAlert(err.response?.data?.message || 'Failed to update', 'danger');
    }
    setEditSaving(false);
  };

  const cancelEdit = () => setEditForm(null);

  // ── Detail panel ───────────────────────────────────────────────────────────
  const renderDetailPanel = () => {
    if (!selectedEmployee) return (
      <div className="text-center text-muted py-5">
        <i className="bi bi-person-lines-fill fs-2 d-block mb-2"></i>
        Select an employee to view details
      </div>
    );

    const e     = selectedEmployee;
    const color = ROLE_COLORS[e.roleKey] || 'secondary';

    return (
      <div>
        {/* Header */}
        <div className="d-flex align-items-center gap-3 mb-3">
          <Avatar user={e} size={56} />
          <div>
            <h6 className="mb-0 fw-bold">{fullName(e)}</h6>
            <span className={`badge bg-${color} mt-1`}>{ROLE_LABELS[e.roleKey] || e.roleKey}</span>
            {' '}
            <span className={`badge ${e.isActive !== false ? 'bg-success' : 'bg-secondary'}`}>
              {e.isActive !== false ? 'Active' : 'Inactive'}
            </span>
          </div>
          {canEdit && !editForm && (
            <button className="btn btn-outline-primary btn-sm ms-auto" onClick={() => openEdit(e)}>
              <i className="bi bi-pencil me-1"></i>Edit
            </button>
          )}
        </div>

        {/* Edit Form */}
        {editForm ? (
          <div className="card border-0 bg-light mb-3">
            <div className="card-body">
              <div className="row g-2">
                <div className="col-6">
                  <label className="form-label form-label-sm">First Name</label>
                  <input className="form-control form-control-sm" value={editForm.firstName}
                    onChange={e => setEditForm(f => ({ ...f, firstName: e.target.value }))} />
                </div>
                <div className="col-6">
                  <label className="form-label form-label-sm">Last Name</label>
                  <input className="form-control form-control-sm" value={editForm.lastName}
                    onChange={e => setEditForm(f => ({ ...f, lastName: e.target.value }))} />
                </div>
                <div className="col-12">
                  <label className="form-label form-label-sm">Email</label>
                  <input className="form-control form-control-sm" type="email" value={editForm.email}
                    onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} />
                </div>
                <div className="col-12">
                  <label className="form-label form-label-sm">Phone</label>
                  <input className="form-control form-control-sm" value={editForm.phone}
                    onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} />
                </div>
                <div className="col-12">
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" id="isActiveChk"
                      checked={editForm.isActive}
                      onChange={e => setEditForm(f => ({ ...f, isActive: e.target.checked }))} />
                    <label className="form-check-label form-label-sm" htmlFor="isActiveChk">Active</label>
                  </div>
                </div>
              </div>
              <div className="d-flex gap-2 mt-2">
                <button className="btn btn-primary btn-sm" onClick={saveEdit} disabled={editSaving}>
                  {editSaving && <span className="spinner-border spinner-border-sm me-1"></span>}Save
                </button>
                <button className="btn btn-secondary btn-sm" onClick={cancelEdit}>Cancel</button>
              </div>
            </div>
          </div>
        ) : (
          <div className="mb-3" style={{ fontSize: 13 }}>
            <div className="row g-1">
              <div className="col-4 text-muted">Email</div>
              <div className="col-8">{e.email}</div>
              {e.phone && <>
                <div className="col-4 text-muted">Phone</div>
                <div className="col-8">{e.phone}</div>
              </>}
              <div className="col-4 text-muted">Joined</div>
              <div className="col-8">{fmt(e.createdAt)}</div>
            </div>
          </div>
        )}

        {/* Leave summary */}
        <div className="border-top pt-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <p className="text-muted mb-0" style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>RECENT LEAVES</p>
            <button
              className="btn btn-link btn-sm p-0"
              onClick={(ev) => { ev.stopPropagation(); handleRefresh(); }}
              title="Refresh leave data"
              style={{ fontSize: 12 }}
            >
              <i className="bi bi-arrow-clockwise" />
            </button>
          </div>
          <LeaveSummary employeeId={e._id} refreshKey={refreshKey} />
        </div>
      </div>
    );
  };

  // ── Main render ────────────────────────────────────────────────────────────
  return (
    <div>
      <PageHeader
        title="Employee Management"
        icon="bi-person-badge"
        subtitle="View and manage your organisation's staff"
      />

      {/* Stats row */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-md-2">
          <div className="card border-0 shadow-sm text-center py-3">
            <div className="fs-4 fw-bold text-primary">{total}</div>
            <div className="text-muted" style={{ fontSize: 12 }}>Total</div>
          </div>
        </div>
        <div className="col-6 col-md-2">
          <div className="card border-0 shadow-sm text-center py-3">
            <div className="fs-4 fw-bold text-success">{active}</div>
            <div className="text-muted" style={{ fontSize: 12 }}>Active</div>
          </div>
        </div>
        {['faculty', 'student', 'manager', 'team_lead'].map(r => (
          <div key={r} className="col-6 col-md-2">
            <div className="card border-0 shadow-sm text-center py-3">
              <div className={`fs-4 fw-bold text-${ROLE_COLORS[r] || 'secondary'}`}>{roleCounts[r] || 0}</div>
              <div className="text-muted text-capitalize" style={{ fontSize: 12 }}>{ROLE_LABELS[r] || r}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Search + Filter */}
      <div className="d-flex gap-2 flex-wrap mb-3">
        <input type="text" className="form-control form-control-sm" style={{ maxWidth: 260 }}
          placeholder="Search by name or email..."
          value={search} onChange={e => setSearch(e.target.value)} />
        <select className="form-select form-select-sm" style={{ width: 180 }}
          value={filterRole} onChange={e => setFilterRole(e.target.value)}>
          <option value="">All Roles</option>
          {ALL_ROLES.map(r => (
            <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>
          ))}
        </select>
        {(search || filterRole) && (
          <button className="btn btn-outline-secondary btn-sm"
            onClick={() => { setSearch(''); setFilterRole(''); }}>
            <i className="bi bi-x-circle me-1"></i>Clear
          </button>
        )}
        {canEdit && (
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={handleRefresh}
            title="Reload leave counts & recent leaves for everyone"
          >
            <i className="bi bi-arrow-clockwise me-1"></i>Refresh Leaves
          </button>
        )}
        <span className="ms-auto align-self-center text-muted" style={{ fontSize: 12 }}>
          {filtered.length} of {total} employees
        </span>
      </div>

      {/* Two-column layout: card grid + detail panel */}
      <div className="row g-3">
        {/* Employee Cards */}
        <div className="col-lg-7">
          {loading ? (
            <LoadingSpinner />
          ) : filtered.length === 0 ? (
            <div className="text-center text-muted py-5">
              <i className="bi bi-people fs-2 d-block mb-2"></i>
              No employees found
            </div>
          ) : (
            <div className="row g-3">
              {filtered.map(emp => {
                const color     = ROLE_COLORS[emp.roleKey] || 'secondary';
                const isActive  = emp.isActive !== false;
                const isSelected = selectedEmployee?._id === emp._id;
                const lc = leaveCounts[emp._id];
                return (
                  <div key={emp._id} className="col-sm-6">
                    <div
                      className={`card border-0 shadow-sm h-100 ${isSelected ? 'border border-primary border-2' : ''}`}
                      style={{ cursor: 'pointer', transition: 'box-shadow .15s', outline: isSelected ? '2px solid #6366f1' : 'none' }}
                      onClick={() => { setSelectedEmployee(emp); setEditForm(null); }}>
                      <div className="card-body d-flex gap-3 align-items-start">
                        <Avatar user={emp} />
                        <div className="flex-grow-1 overflow-hidden">
                          <div className="fw-semibold text-truncate" style={{ fontSize: 14 }}>
                            {fullName(emp)}
                            {lc?.onLeaveToday && (
                              <span className="badge bg-info text-dark ms-2" style={{ fontSize: 10 }} title="Currently on approved leave">
                                <i className="bi bi-calendar-x me-1" />On leave today
                              </span>
                            )}
                          </div>
                          <div className="text-muted text-truncate" style={{ fontSize: 12 }}>{emp.email}</div>
                          <div className="mt-1 d-flex gap-1 flex-wrap">
                            <span className={`badge bg-${color}`} style={{ fontSize: 10 }}>
                              {ROLE_LABELS[emp.roleKey] || emp.roleKey}
                            </span>
                            <span className={`badge ${isActive ? 'bg-success' : 'bg-secondary'}`} style={{ fontSize: 10 }}>
                              {isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          {/* Leave counts badges — admin/manager only */}
                          {lc && lc.total > 0 && (
                            <div className="mt-1 d-flex gap-1 flex-wrap" title={`${lc.totalDays} days total this year`}>
                              {lc.proposed > 0 && (
                                <span className="badge bg-warning text-dark" style={{ fontSize: 10 }}>
                                  <i className="bi bi-hourglass-split me-1" />{lc.proposed} pending
                                </span>
                              )}
                              {lc.approved > 0 && (
                                <span className="badge bg-success" style={{ fontSize: 10 }}>
                                  <i className="bi bi-check-circle me-1" />{lc.approved} approved
                                </span>
                              )}
                              {lc.rejected > 0 && (
                                <span className="badge bg-danger" style={{ fontSize: 10 }}>
                                  <i className="bi bi-x-circle me-1" />{lc.rejected} rejected
                                </span>
                              )}
                            </div>
                          )}
                          {emp.phone && (
                            <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                              <i className="bi bi-telephone me-1"></i>{emp.phone}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Detail Panel */}
        <div className="col-lg-5">
          <div className="card border-0 shadow-sm" style={{ position: 'sticky', top: 16 }}>
            <div className="card-body">
              {renderDetailPanel()}
            </div>
          </div>
        </div>
      </div>

      <AlertToast show={alert.show} message={alert.msg} type={alert.type}
        onClose={() => setAlert(a => ({ ...a, show: false }))} />
    </div>
  );
}
